// Copyright (c) Microsoft. All rights reserved.

import { AuthConfig } from '../../../libs/auth/AuthHelper';
import { AlertType } from '../../../libs/models/AlertType';
import { IChatUser } from '../../../libs/models/ChatUser';
import { ServiceInfo } from '../../../libs/models/ServiceInfo';
import { TokenUsage } from '../../../libs/models/TokenUsage';

// This is the default user information when authentication is set to 'None'.
// It must match what is defined in PassthroughAuthenticationHandler.cs on the backend.
export const DefaultChatUser: IChatUser = {
    id: 'c05c61eb-65e4-4223-915a-fe72b0c9ece1',
    emailAddress: 'user@contoso.com',
    fullName: 'Default User',
    online: true,
    isTyping: false,
};

export const DefaultActiveUserInfo: ActiveUserInfo = {
    id: DefaultChatUser.id,
    email: DefaultChatUser.emailAddress,
    username: DefaultChatUser.fullName,
};

export interface ActiveUserInfo {
    id: string;
    email: string;
    username: string;
}

export interface Alert {
    message: string;
    type: AlertType;
    id?: string;
    onRetry?: () => void;
}

interface Feature {
    enabled: boolean; // Whether to show the feature in the UX
    label: string;
    inactive?: boolean; // Set to true if you don't want the user to control the visibility of this feature or there's no backend support
    description?: string;
}

export interface Setting {
    title: string;
    description?: string;
    features: FeatureKeys[];
    stackVertically?: boolean;
    learnMoreLink?: string;
}

/**
 * Tracks the lifecycle state of authentication token acquisition.
 * - 'uninitialized': App just started, no token acquisition attempted yet
 * - 'acquiring': Token fetch is in progress (Teams or MSAL)
 * - 'ready': Token has been successfully acquired and is available
 * - 'failed': Token acquisition failed, authentication required action
 */
export type TokenReadinessState = 'uninitialized' | 'acquiring' | 'ready' | 'failed';

export interface AppState {
    alerts: Alert[];
    activeUserInfo?: ActiveUserInfo;
    authConfig?: AuthConfig | null;
    tokenUsage: TokenUsage;
    features: Record<FeatureKeys, Feature>;
    settings: Setting[];
    serviceInfo: ServiceInfo;
    isMaintenance: boolean;
    tokenReadiness: TokenReadinessState;
}

export enum FeatureKeys {
    DarkMode,
    SimplifiedExperience,
    AzureContentSafety,
    AzureAISearch,
    BotAsDocs,
    RLHF, // Reinforcement Learning from Human Feedback
}

export const Features = {
    [FeatureKeys.DarkMode]: {
        enabled: false,
        label: 'Karanlık Mod',
    },
    [FeatureKeys.SimplifiedExperience]: {
        enabled: true,
        label: 'Basit Sohbet Deneyimi',
    },
    [FeatureKeys.AzureContentSafety]: {
        enabled: false,
        label: 'Azure İçerik Güvenliği',
        inactive: true,
    },
    [FeatureKeys.AzureAISearch]: {
        enabled: false,
        label: 'Azure AI Arama',
        inactive: true,
    },
    [FeatureKeys.BotAsDocs]: {
        enabled: false,
        label: 'Sohbet Oturumlarını İçe Aktar',
    },
    [FeatureKeys.RLHF]: {
        enabled: false,
        label: 'İnsan Geribesleme Tabanlı Geri Bildirim',
        description: 'Model tarafından üretilen yanıtlarınızı oylama yeteneği. Sadece gösterim amaçlı.',
        // TODO: [Issue #42] Send and store feedback in backend
    },
};

export const Settings = [
    {
        // Basic settings has to stay at the first index. Add all new settings to end of array.
        title: 'Temel',
        features: [FeatureKeys.DarkMode],
        stackVertically: true,
    },
    {
        title: 'Görünüm',
        features: [FeatureKeys.SimplifiedExperience],
        stackVertically: true,
    },
    // {
    //     title: 'Azure AI Arama',
    //     features: [FeatureKeys.AzureContentSafety, FeatureKeys.AzureAISearch],
    //     stackVertically: true,
    // },
];

export const initialState: AppState = {
    alerts: [],
    activeUserInfo: DefaultActiveUserInfo,
    authConfig: {} as AuthConfig,
    tokenUsage: {},
    features: Features,
    settings: Settings,
    serviceInfo: {
        memoryStore: { types: [], selectedType: '' },
        version: '',
        isContentSafetyEnabled: false,
    },
    isMaintenance: false,
    tokenReadiness: 'uninitialized',
};
